import { BaseUITools } from "@/uiControls/base/baseUITools";
import { VisionaryScene } from "@/visionaryControllers/visionaryScene";
import { ViewHelperTools3D } from "@/uiControls/viewhelper-tools-3d";
import { ModelViewPanelTools } from "@/uiControls/panelControls/modelview-panel-tools";
import { CameraController } from "@/visionaryControllers/cameraController";
import { AnimationController } from "@/visionaryControllers/animationController";
import { ViewHelperController } from "@/visionaryControllers/viewhelperController";
import { globalManager, globalTools } from "@/utils/globalConfig";
import * as THREE from "three";
import { sceneUtils } from "@/utils/sceneUtils";

/**
 * 场景视图面板，内置VisionaryScene实例，作为场景视图渲染容器
 * 增加viewhelper-panel-tools，作为场景视图辅助工具容器
 * 增加modelview-panel-tools，作为场景视图模型列表管理控制器
 * 后续可以增加trans-tools，作为场景视图变换工具箱
 * 负责创建和管理场景视图的渲染容器
 */
export class SceneViewPanelTools extends BaseUITools {
  private viewContainer: HTMLElement | null = null;
  private id: string;
  private containerId: string;
  private mainscene!: VisionaryScene;
  private viewHelperTools!: ViewHelperTools3D;
  private modelViewPanelTools!: ModelViewPanelTools;
  private cameraController!: CameraController; // 相机控制器
  private animationController!: AnimationController; // 动画控制器
  private viewHelperController!: ViewHelperController; // 视图辅助工具控制器
  private viewHelperContainerId: string = "";
  private modelViewContainerId: string = "";
  private fpsHudContainerId: string = "";
  private fpsHudElement: HTMLElement | null = null;
  private fpsHudValueElement: HTMLElement | null = null;
  private fpsHudMsElement: HTMLElement | null = null;
  // private gizmoHelperContainerId: string = '';
  private initPromise!: Promise<void>;
  private readonly boundFpsUpdate = (fps: number | null) =>
    this.updateFpsHud(fps);

  constructor(id: string) {
    super(id, false);
    this.id = id;
    this.containerId = `${this.id}_vscene_view`;
    this.viewHelperContainerId = `${this.id}_viewhelper`;
    this.modelViewContainerId = `${this.id}_modelview`;
    this.fpsHudContainerId = `${this.id}_fps_hud`;
    // this.gizmoHelperContainerId = `${this.id}_gizmohelper`;
    this.initPanel();
    this.initPromise = this.initPanelScene();
  }

  initPanel() {
    // 创建主容器
    const panelContainerElement = this.ui.createElement({
      tag: "div",
      className: "panel-setting-container",
    });

    // 创建场景视窗容器
    const sceneViewElement = this.ui.createElement({
      tag: "div",
      className: "vision-sceneview-container",
      id: this.containerId,
    });
    // sceneViewElement.classList.add('video-exporting-active'); // 调试样式用
    this.viewContainer = sceneViewElement;

    // 创建viewhelper容器（绝对定位，右上角）
    const viewHelperContainer = this.ui.createElement({
      tag: "div",
      className: "vision-viewhelper-container",
      id: this.viewHelperContainerId,
    });
    // 创建gizmo容器（绝对定位，下方居中）
    // const gizmoHelperContainer = this.ui.createElement({
    //     tag: 'div',
    //     className: 'vision-gizmohelper-container',
    //     id: this.gizmoHelperContainerId
    // });

    const modelViewContainer = this.ui.createElement({
      tag: "div",
      className: "vision-modelview-container",
      id: this.modelViewContainerId,
    });

    // console.log("init-------Panel", this.containerId);
    panelContainerElement.appendChild(sceneViewElement);
    panelContainerElement.appendChild(viewHelperContainer);
    // panelContainerElement.appendChild(gizmoHelperContainer);
    panelContainerElement.appendChild(modelViewContainer);

    if (import.meta.env.DEV) {
      panelContainerElement.appendChild(this.createFpsHud());
    }
    this.appendChild("panelContainer", panelContainerElement);
  }

  async initPanelScene() {
    // console.log("init Panel Scene-------Panel", this.containerId);
    // console.log("get container----", document.getElementById(this.containerId));

    // 使用轮询等待容器元素出现（最多等待 3 秒）
    let retryCount = 0;
    const maxRetries = 60; // 60 * 50ms = 3秒
    while (
      !document.getElementById(this.containerId) &&
      retryCount < maxRetries
    ) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      retryCount++;
    }

    if (!document.getElementById(this.containerId)) {
      const error = new Error(
        `容器元素 ${this.containerId} 未找到，无法初始化场景`
      );
      console.error(error);
      throw error;
    }

    // 初始化场景
    const mainscene = new VisionaryScene(this.containerId);
    await mainscene.waitForInit();
    this.mainscene = mainscene;

    // 注意：不要在这里覆盖背景，让天空盒背景正常显示
    // 如果需要设置背景，应该在 VisionaryScene 的 setupHDRSkybox 中处理
    // 如果天空盒加载失败，可以在那里设置默认背景色

    // 初始化相机控制器（关键：为每个视窗绑定鼠标事件）
    this.cameraController = new CameraController(this.mainscene);
    this.animationController = new AnimationController(this.mainscene);
    this.animationController.setHandleUpdateKeyframe((keyframes: any[]) => {
      // 设置更新关键帧回调函数，同步更新timeline面板
      globalTools.timelinePanelTools?.updateTimelineWithKeyframes(
        keyframes,
        false
      );
    });

    // 初始化viewhelper工具
    // this.viewHelperTools = new ViewHelperTools(this.viewHelperContainerId, this.mainscene);
    this.modelViewPanelTools = new ModelViewPanelTools(
      this.modelViewContainerId,
      this.mainscene
    );
    // 传递 cameraController 以便 ViewHelperTools3D 可以同步相机状态
    this.viewHelperTools = new ViewHelperTools3D(
      this.viewHelperContainerId,
      this.mainscene,
      this.cameraController
    );
    // 创建 ViewHelperController（ViewHelperTools3D 内部也有，但我们需要一个独立的实例用于 sceneInfo）
    this.viewHelperController = new ViewHelperController(this.mainscene);

    this.mainscene.setObjectChangeCallback(() => {
      // 添加对象回调，对象列表回显
      this.modelViewPanelTools?.updateModelList();
    });
    this.mainscene.setSceneResetCallback(() => {
      // 场景重置回调，重置录制相机数量
      this.modelViewPanelTools?.resetRecordingCameraNumber();
      sceneUtils.resetEnvironment();
    });

    this.cameraController.setUpdateControlsDisplayCallback(() => {
      // ViewHelperTools3D 会在渲染循环中自动更新，无需手动调用
    });

    this.modelViewPanelTools.onModelSelect((id, sceneid) => {
      //   console.log("modelview panel tools model select", id, sceneid);
      // 需要更新globalManager中当前选中的对象
      globalManager.setCurrentSelected(sceneid, id);
    });
    if (import.meta.env.DEV) {
      this.mainscene.setOnFPSUpdate(this.boundFpsUpdate);
    }
    this.mainscene.onWindowResize();

    const sceneInfo = {
      scene: this.mainscene,
      cameraController: this.cameraController,
      animationController: this.animationController,
      fileController: this.modelViewPanelTools.getFileController(),
      viewHelperController: this.viewHelperController,
    };
    globalManager.addSceneView(this.containerId, sceneInfo);
  }

  getSceneView(): VisionaryScene | null {
    if (this.mainscene) {
      return this.mainscene;
    }
    return null;
  }

  /**
   * 等待场景初始化完成（包括相机控制器）
   */
  public async waitForSceneInit(): Promise<void> {
    // 等待 initPanelScene() 完成，这包括场景和相机控制器的初始化
    await this.initPromise;
  }

  /**
   * 获取场景实例
   */
  public getScene(): VisionaryScene {
    return this.mainscene;
  }

  /**
   * 获取相机控制器实例
   */
  public getCameraController(): CameraController {
    return this.cameraController;
  }

  /**
   * 获取动画控制器实例
   */
  public getAnimationController(): AnimationController {
    return this.animationController;
  }

  /**
   * 加载场景
   * @param sceneData 场景数据
   */
  public loadSceneByJson(sceneData: any): void {
    this.animationController.replaceGlobalKeyframes([]);
    sceneUtils.loadSceneByJson(
      sceneData,
      this.mainscene,
      this.animationController
    );
  }

  /**
   * 开发用渲染帧率 HUD（不进时间轴，生产构建不创建）
   */
  private createFpsHud(): HTMLElement {
    const fpsHud = this.ui.createElement({
      tag: "div",
      className: "vision-fps-hud",
      id: this.fpsHudContainerId,
      attributes: {
        title: "Render FPS (dev)",
      },
      data: {
        level: "idle",
      },
    });
    const fpsValue = this.ui.createElement({
      tag: "span",
      className: "vision-fps-hud-value",
      textContent: "--",
    });
    const fpsUnit = this.ui.createElement({
      tag: "span",
      className: "vision-fps-hud-unit",
      textContent: "FPS",
    });
    const fpsMs = this.ui.createElement({
      tag: "span",
      className: "vision-fps-hud-ms",
      textContent: "",
    });
    fpsHud.appendChild(fpsValue);
    fpsHud.appendChild(fpsUnit);
    fpsHud.appendChild(fpsMs);
    this.fpsHudElement = fpsHud;
    this.fpsHudValueElement = fpsValue;
    this.fpsHudMsElement = fpsMs;
    return fpsHud;
  }

  private updateFpsHud(fps: number | null): void {
    if (!this.fpsHudElement || !this.fpsHudValueElement) {
      return;
    }
    if (fps === null || !Number.isFinite(fps) || fps <= 0) {
      this.fpsHudValueElement.textContent = "--";
      if (this.fpsHudMsElement) {
        this.fpsHudMsElement.textContent = "";
      }
      this.fpsHudElement.dataset.level = "idle";
      return;
    }
    this.fpsHudValueElement.textContent = String(fps);
    if (this.fpsHudMsElement) {
      this.fpsHudMsElement.textContent = `${(1000 / fps).toFixed(1)} ms`;
    }
    this.fpsHudElement.dataset.level =
      fps >= 50 ? "good" : fps >= 30 ? "warn" : "bad";
  }

  /**
   * 销毁场景视图面板并释放所有资源
   */
  public destroy(): void {
    // console.log("destroying SceneViewPanelTools:", this.id);

    if (this.mainscene && typeof this.mainscene.setOnFPSUpdate === "function") {
      this.mainscene.setOnFPSUpdate(null);
    }

    // 1. 销毁各种工具和控制器
    if (
      this.viewHelperTools &&
      typeof this.viewHelperTools.dispose === "function"
    ) {
      try {
        this.viewHelperTools.dispose();
      } catch (error) {
        console.warn("清理 ViewHelperTools 失败:", error);
      }
    }

    if (
      this.modelViewPanelTools &&
      typeof this.modelViewPanelTools.destroy === "function"
    ) {
      try {
        this.modelViewPanelTools.destroy();
      } catch (error) {
        console.warn("清理 ModelViewPanelTools 失败:", error);
      }
    }

    if (
      this.cameraController &&
      typeof this.cameraController.destroy === "function"
    ) {
      try {
        this.cameraController.destroy();
      } catch (error) {
        console.warn("清理 CameraController 失败:", error);
      }
    }

    // 2. 从 globalManager 中移除场景视图
    // Note: GlobalManager 没有 removeSceneView 方法，需要根据实际需求添加
    // 或者使用 globalManager 的内部方法（需要检查是否有对应的访问方法）
    globalManager.removeSceneViewById(this.containerId);

    // 3. 销毁主场景（这会触发 vinSceneDestroy 事件）
    if (this.mainscene && typeof this.mainscene.destroy === "function") {
      try {
        this.mainscene.destroy();
      } catch (error) {
        console.warn("清理 VisionaryScene 失败:", error);
      }
    }

    // 4. 清理引用
    this.viewHelperTools = null!;
    this.modelViewPanelTools = null!;
    this.cameraController = null!;
    this.animationController = null!;
    this.viewHelperController = null!;
    this.mainscene = null!;
    this.fpsHudElement = null;
    this.fpsHudValueElement = null;
    this.fpsHudMsElement = null;

    // 5. 调用基类的销毁方法
    super.destroy();

    // console.log("SceneViewPanelTools destroyed:", this.id);
  }
}
